import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { fallback } from '@/lib/utils/zodFallback';
import { ConnectCompanyDialog } from '../-components/ConnectCompanyDialog';
import { EntityScreeningTable } from '../-components/EntityScreeningTable';
import { FlagConfirmationDialog } from '../-components/FlagConfirmationDialog';
import { useEntityScreeningController } from '../-hooks/useEntityScreeningController';

const entityScreeningSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(500), 20),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
  tab: z.string().optional(),
});

export const Route = createFileRoute('/(client)/entity-screening/(pages)/')({
  validateSearch: entityScreeningSearch,
  component: EntityScreeningPage,
});

function EntityScreeningPage() {
  const ctrl = useEntityScreeningController();

  return (
    <PageContainer>
      <EntityScreeningTable
        data={ctrl.items}
        isFetching={ctrl.isFetching}
        totalCount={ctrl.meta?.totalCount ?? 0}
        table={ctrl.table}
        rowSelection={ctrl.rowSelection}
        onRowSelectionChange={ctrl.setRowSelection}
        quickFilter={ctrl.quickFilter}
        onQuickFilterChange={ctrl.setQuickFilter}
        onDismiss={ctrl.handleDismiss}
        onRequestFlag={ctrl.requestFlag}
        onConnect={ctrl.openConnectDialog}
        isValidating={ctrl.isValidating}
        countryOptions={ctrl.countryOptions}
        facilityProcessOptions={ctrl.facilityProcessOptions}
        threshold={ctrl.threshold}
        isThresholdLoading={ctrl.isThresholdLoading}
        isUpdatingThreshold={ctrl.isUpdatingThreshold}
        onUpdateThreshold={ctrl.handleUpdateThreshold}
        canEdit={ctrl.canEdit}
        canConnect={ctrl.canConnect}
      />

      <FlagConfirmationDialog
        open={ctrl.flagDialogOpen}
        onOpenChange={ctrl.setFlagDialogOpen}
        onConfirm={ctrl.confirmFlag}
        count={ctrl.pendingFlagIds.length}
      />

      <ConnectCompanyDialog
        open={ctrl.connectDialog.open}
        onOpenChange={ctrl.setConnectDialogOpen}
        companyName={ctrl.connectDialog.companyName}
        onConfirm={ctrl.confirmConnect}
        isLoading={ctrl.isConnecting}
      />
    </PageContainer>
  );
}
